import { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, MessageSquare, ShieldCheck, Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';

export default function AIPage() {
  const [tab, setTab] = useState('generate');

  // SMS Generation
  const [genProduct, setGenProduct] = useState('');
  const [genDesc, setGenDesc] = useState('');
  const [genTone, setGenTone] = useState('przyjazny i profesjonalny');
  const [genCount, setGenCount] = useState(3);
  const [genResult, setGenResult] = useState('');
  const [genLoading, setGenLoading] = useState(false);

  // Classification
  const [classMsg, setClassMsg] = useState('');
  const [classPhone, setClassPhone] = useState('');
  const [classResult, setClassResult] = useState(null);
  const [classLoading, setClassLoading] = useState(false);

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      const r = await api.post('/ai/generate-sms', {
        product_name: genProduct,
        product_description: genDesc,
        tone: genTone,
        count: genCount,
      });
      setGenResult(r.data.variants);
      toast.success('Wygenerowano warianty SMS');
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad AI'); }
    finally { setGenLoading(false); }
  };

  const handleClassify = async () => {
    setClassLoading(true);
    try {
      const r = await api.post('/ai/classify-response', { message: classMsg, phone: classPhone });
      setClassResult(r.data);
      toast.success('Wiadomosc sklasyfikowana');
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad AI'); }
    finally { setClassLoading(false); }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Skopiowano'); };

  const CLASS_COLORS = {
    OPT_OUT: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]',
    INTERESTED: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]',
    QUESTION: 'bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]',
    NEUTRAL: 'bg-muted text-muted-foreground',
    SPAM: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]',
  };

  return (
    <div className="space-y-6" data-testid="ai-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Asystent AI</h1>
        <p className="text-sm text-muted-foreground mt-1">Generowanie tresci i analiza wiadomosci</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="generate" data-testid="ai-tab-generate"><Sparkles className="h-3.5 w-3.5 mr-1.5" />Generator SMS</TabsTrigger>
          <TabsTrigger value="classify" data-testid="ai-tab-classify"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" />Klasyfikacja</TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Parametry</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nazwa produktu</Label>
                  <Input value={genProduct} onChange={e => setGenProduct(e.target.value)} placeholder="np. Kurs Marketing Pro" data-testid="ai-gen-product" />
                </div>
                <div className="space-y-2">
                  <Label>Opis produktu</Label>
                  <Textarea value={genDesc} onChange={e => setGenDesc(e.target.value)} placeholder="Krotki opis oferty..." rows={3} data-testid="ai-gen-desc" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Ton</Label>
                    <Input value={genTone} onChange={e => setGenTone(e.target.value)} data-testid="ai-gen-tone" />
                  </div>
                  <div className="space-y-2">
                    <Label>Liczba wariantow</Label>
                    <Input type="number" min={1} max={5} value={genCount} onChange={e => setGenCount(parseInt(e.target.value) || 3)} data-testid="ai-gen-count" />
                  </div>
                </div>
                <Button onClick={handleGenerate} disabled={genLoading || !genProduct} className="w-full" data-testid="ai-gen-submit">
                  {genLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  Generuj warianty SMS
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wynik</CardTitle></CardHeader>
              <CardContent>
                {genResult ? (
                  <div className="space-y-3">
                    <div className="whitespace-pre-wrap text-sm font-mono p-4 rounded-lg bg-muted/30 border border-border/30" data-testid="ai-gen-result">
                      {genResult}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => copyText(genResult)}><Copy className="h-3.5 w-3.5 mr-1.5" />Kopiuj</Button>
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground text-sm">
                    <Sparkles className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />
                    Wypelnij parametry i kliknij Generuj
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="classify" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wiadomosc do klasyfikacji</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Tresc wiadomosci</Label>
                  <Textarea value={classMsg} onChange={e => setClassMsg(e.target.value)} placeholder='np. "Prosze mnie wiecej nie niszczyc, STOP"' rows={4} data-testid="ai-class-message" />
                </div>
                <div className="space-y-2">
                  <Label>Numer telefonu (opcj. - do auto-blacklist)</Label>
                  <Input value={classPhone} onChange={e => setClassPhone(e.target.value)} placeholder="+48..." data-testid="ai-class-phone" />
                </div>
                <Button onClick={handleClassify} disabled={classLoading || !classMsg} className="w-full" data-testid="ai-class-submit">
                  {classLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Klasyfikuj wiadomosc
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border/50 bg-card/80">
              <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wynik klasyfikacji</CardTitle></CardHeader>
              <CardContent>
                {classResult ? (
                  <div className="space-y-4" data-testid="ai-class-result">
                    <div className="flex items-center gap-3">
                      <Badge className={`text-sm px-3 py-1 ${CLASS_COLORS[classResult.classification] || CLASS_COLORS.NEUTRAL}`}>
                        {classResult.classification}
                      </Badge>
                      {classResult.auto_blacklisted && <Badge variant="destructive" className="text-xs">Auto-blacklisted</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{classResult.reason}</p>
                    {classResult.auto_blacklisted && (
                      <div className="p-3 rounded-lg bg-[hsl(var(--destructive))]/5 border border-[hsl(var(--destructive))]/20">
                        <p className="text-xs text-[hsl(var(--destructive))]">Numer automatycznie dodany do czarnej listy (OPT_OUT)</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-12 text-center text-muted-foreground text-sm">
                    <ShieldCheck className="h-8 w-8 mx-auto mb-3 text-muted-foreground/30" />
                    Wklej wiadomosc i kliknij Klasyfikuj
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
